import { useCallback } from "react";
import CloseIconButton from "@entrilia/shared/components/CloseIconButton.tsx";
import { ValidationItemContainer } from "../../../../reporting/report-lists/reports/copyto/CompanyMultipleReportsListItemContainer.tsx";
import { Failed } from "../../../../reporting/report-lists/reports/copyto/controls/Failed.tsx";
import { ReportInProgress } from "../../../../reporting/report-lists/reports/copyto/controls/ReportInProgress.tsx";
import ReportIsNotValid from "../../../../reporting/report-lists/reports/copyto/controls/ReportIsNotValid.tsx";
import { WaitingForValidation } from "../../../../reporting/report-lists/reports/copyto/controls/WaitingForValidation.tsx";
import type { SelectedCompanyTile } from "../../../hooks/validationStateSlice.ts";
import type { DashboardValidationType } from "../../hooks/useDashboardValidation.ts";
import { ReportNotConfigured, ReportValidated } from "../index.ts";

interface Props {
  tile: SelectedCompanyTile;
  validation: DashboardValidationType;
}

export const SmartViewTileItemValidation = ({ tile, validation }: Props) => {
  const report = tile.report;
  const active =
    tile.companyCode === validation.activeCompanyTile?.companyCode &&
    validation.activeCompanyTile?.tile.tileId === tile.tile.tileId;

  const onSelected = useCallback(() => {
    validation.setActiveCompanyTile(tile);
  }, [validation, tile]);

  const onRemove = useCallback(() => {
    validation.removeTile(tile);
  }, [validation, tile]);

  if (!report) {
    return <ReportNotConfigured companyTile={tile} />;
  }

  return (
    <ValidationItemContainer
      caption={report.name}
      active={active}
      completed={tile.validated}
      selectable
      onSelected={onSelected}
      TopAction={<CloseIconButton onClick={onRemove} color="secondary" size="small" />}
    >
      {!tile.validating && !tile.validated && !tile.error && <WaitingForValidation />}
      {tile.validating && (
        <ReportInProgress text="Please wait while we check if the report can be copied within the company..." />
      )}
      {tile.validated && tile.result?.hasRequiredDataset === false && (
        <ReportIsNotValid
          errorMessage={`Dataset ${tile.result?.requiredDataSetName ?? ""} is missing in the target company.`}
        />
      )}
      {tile.validated && tile.result?.hasRequiredDataset && <ReportValidated companyTile={tile} />}
      {tile.error && <Failed onRetry={() => validation.retryValidation(tile)} />}
    </ValidationItemContainer>
  );
};
